import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';

import useErrorTranslation from '@/utils/hooks/language/useErrorTranslation';
import useUser from '@/utils/hooks/swr/useUser';
import useUserMarketing from '@/utils/hooks/swr/useUserMarketing';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { ErrorCustom } from '@/utils/client';
import { put } from '@/utils/client';
import { useSignUpStore } from '@/utils/zustand/providers/sign-up-provider';
import useStateWithReset from '@/utils/hooks/utils/useStateWithReset';

import { SIGN_UP } from '@/lib/routes/privateRoutes';
import type { UserInfosFormType } from '@/lib/signup/userInfos/formBusinessRules';
import {
    computeFormDefaults,
    isStepThreeFormComplete,
    transformFormDataForSubmit
} from '@/lib/signup/userInfos/formBusinessRules';
import type { SubStepType } from '@/lib/signup/userInfos/subSteps';
import { subSteps } from '@/lib/signup/userInfos/subSteps';

import { USER_MARKETING, USER_PROFILE_INFOS } from '@/routes/apiEndpoints';

type UseUserInfosFormLogicParams = {
    currentSubStep: SubStepType;
    setCurrentSubStep: (arg: SubStepType) => void;
};

export const useUserInfosFormLogic = ({
    currentSubStep,
    setCurrentSubStep
}: UseUserInfosFormLogicParams) => {
    const router = useRouter();
    const { t } = useTranslation('signup_funnel');
    const { t_error } = useErrorTranslation();
    const [globalError, setGlobalError, resetGlobalError] = useStateWithReset(null);
    const { updateCurrentStep, setHasUserInfosSubmittedSuccessfully } = useSignUpStore(
        (state) => state
    );
    const { user, mutateUser } = useUser();
    const { userMarket, mutateUserMarket } = useUserMarketing();

    const computedDefaults = useMemo<UserInfosFormType>(
        () => computeFormDefaults(user, userMarket),
        [user, userMarket]
    );

    const formMethods = useForm<UserInfosFormType>({
        defaultValues: computedDefaults
    });

    useEffect(() => {
        formMethods.reset(computedDefaults);
    }, [computedDefaults]);

    const {
        setError,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = formMethods;

    const watchedCompany = formMethods.watch('company');
    const watchedBusinessType = formMethods.watch('business_type');
    const watchedCompanyPeopleCount = formMethods.watch('company_people_count');
    const watchedCompanyIndustry = formMethods.watch('company_industry');
    const watchedIsFranchise = formMethods.watch('is_franchise');

    const isStepThreeComplete = useMemo(() => {
        if (currentSubStep.id !== 3) return true;

        return isStepThreeFormComplete({
            company: watchedCompany,
            business_type: watchedBusinessType,
            company_people_count: watchedCompanyPeopleCount,
            company_industry: watchedCompanyIndustry,
            is_franchise: watchedIsFranchise
        });
    }, [
        currentSubStep.id,
        watchedCompany,
        watchedBusinessType,
        watchedCompanyPeopleCount,
        watchedCompanyIndustry,
        watchedIsFranchise
    ]);

    const goToSubStep = (subStepId: SubStepType['id']) => {
        setCurrentSubStep(subSteps.find((subStep) => subStep.id === subStepId));
        const layoutScrollableContent = document.getElementById('layout-scrollable-content');
        if (layoutScrollableContent) {
            layoutScrollableContent.scrollTop = 0;
        }
    };

    const displaySpecificErrors = (errorsFields: ErrorCustom['response']['data']['errors']) => {
        const fields = Object.keys(errorsFields);
        const normalizeField = (field: string): keyof UserInfosFormType =>
            field === 'settings.timezone' ? 'timezone' : (field as keyof UserInfosFormType);

        fields.forEach((field: keyof UserInfosFormType | 'settings.timezone') => {
            const normalizedField = normalizeField(field);
            const error_type = errorsFields[field][0];
            setError(normalizedField, {
                type: 'manual',
                message: t_error(
                    `signup_funnel:user_infos.errors.error_${normalizedField}_${error_type}`
                )
            });
        });

        let firstSubStepWithErrorId: SubStepType['id'] = null;
        subSteps.forEach((subStep) => {
            if (
                !firstSubStepWithErrorId &&
                fields.some((field) => subStep.fields.includes(normalizeField(field)))
            ) {
                firstSubStepWithErrorId = subStep.id;
            }
        });
        if (firstSubStepWithErrorId) {
            goToSubStep(firstSubStepWithErrorId);
        }
    };

    const displayErrors = (e: ErrorCustom) => {
        if (e.status === 422) {
            const errorsFields = e.response?.data?.errors;
            if (errorsFields) {
                displaySpecificErrors(errorsFields);
            } else {
                setGlobalError(t('errors:error_occured_submit_data'));
            }
        } else {
            setGlobalError(t('errors:error_occured_submit_data'));
        }
    };

    const onSubmit = async (data: UserInfosFormType) => {
        resetGlobalError();

        const { dataMarket, dataUser } = transformFormDataForSubmit(data);

        try {
            await Promise.all([put(USER_PROFILE_INFOS, dataUser), put(USER_MARKETING, dataMarket)]);
            await Promise.all([
                mutateUser(),
                mutateUserMarket({ ...userMarket, ...dataMarket }, { revalidate: false })
            ]);
            setHasUserInfosSubmittedSuccessfully(true);

            if (window?.dataLayer && !user?.first_name) {
                window.dataLayer.push({
                    event: 'add_social_profiles'
                });
            }

            updateCurrentStep('USER_SET_UP', user.id);
            await router.push(SIGN_UP.USER_SET_UP);
        } catch (error) {
            displayErrors(error);
        }
    };

    return {
        formMethods,
        isStepThreeComplete,
        isSubmitting,
        isValid,
        globalError,
        setGlobalError,
        onSubmit: handleSubmit(onSubmit),
        goToSubStep
    };
};
